import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/axiosConfig';
import { useSetup } from '../context/SetupContext';
import '../styles/ReservationTypesPage.css';

function buildDurationOptions() {
  const options = [];
  for (let m = 5; m <= 240; m += 5) {
    const hours = Math.floor(m / 60);
    const mins = m % 60;
    let label;
    if (hours === 0) {
      label = `${mins} دقيقة`;
    } else if (mins === 0) {
      label = hours === 1 ? 'ساعة' : hours === 2 ? 'ساعتين' : `${hours} ساعات`;
    } else {
      const hPart = hours === 1 ? 'ساعة' : hours === 2 ? 'ساعتين' : `${hours} ساعات`;
      label = `${hPart} و ${mins} دقيقة`;
    }
    options.push({ value: m, label });
  }
  return options;
}

const DURATION_OPTIONS = buildDurationOptions();

function formatDuration(minutes) {
  if (!minutes) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} دقيقة`;
  const hh = String(h);
  const mm = String(m).padStart(2, '0');
  if (m === 0) {
    return h === 1 ? 'ساعة' : h === 2 ? 'ساعتين' : `${h} ساعات`;
  }
  return `${hh}:${mm}`;
}

export default function ReservationTypesPage() {
  const setup = useSetup();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', price: '', duration_minutes: '30' });

  const fetchTypes = async () => {
    try {
      const { data } = await api.get('/backoffice/reservation-types');
      if (data.success) {
        setTypes(data.reservation_types);
        setup?.setHasTypes((data.reservation_types || []).length > 0);
      }
    } catch {
      toast.error('خطأ في تحميل أنواع الحجز');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTypes(); }, []);

  const resetForm = () => {
    setForm({ name: '', price: '', duration_minutes: '30' });
    setEditingId(null);
  };

  const startEdit = (rt) => {
    setEditingId(rt.id);
    setForm({
      name: rt.name,
      price: String(rt.price),
      duration_minutes: String(rt.duration_minutes || 30),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('أدخل اسم نوع الحجز');

    const payload = {
      name: form.name,
      price: parseFloat(form.price) || 0,
      duration_minutes: parseInt(form.duration_minutes) || 30,
    };

    try {
      if (editingId) {
        const { data } = await api.put(`/backoffice/reservation-types/${editingId}`, payload);
        if (data.success) {
          toast.success(data.message);
          resetForm();
          fetchTypes();
        } else {
          toast.error(data.message);
        }
      } else {
        const { data } = await api.post('/backoffice/reservation-types', payload);
        if (data.success) {
          toast.success(data.message);
          resetForm();
          fetchTypes();
        } else {
          toast.error(data.message);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف نوع الحجز؟')) return;
    try {
      const { data } = await api.delete(`/backoffice/reservation-types/${id}`);
      if (data.success) {
        toast.success(data.message);
        fetchTypes();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    }
  };

  if (loading) return <div className="page-loading">جاري التحميل...</div>;

  return (
    <div className="types-page">
      <h1>أنواع الحجز</h1>

      {types.length === 0 && (
        <div className="setup-prompt">
          <span className="setup-prompt-icon">&#128075;</span>
          <div>
            <h2>مرحباً بك! لنبدأ الإعداد</h2>
            <p>
              لبدء استخدام لوحة التحكم، أضف نوع حجز واحد على الأقل (مثل: قص شعر، حلاقة ذقن...).
              بعد إضافة أول نوع حجز ستتمكن من الوصول لبقية الصفحات.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="type-form">
        <input
          placeholder="اسم نوع الحجز"
          value={form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
        />
        <input
          type="number"
          placeholder="السعر"
          value={form.price}
          onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
          dir="ltr"
          min="0"
          step="0.01"
        />
        <select
          className="duration-select"
          value={form.duration_minutes}
          onChange={(e) => setForm((p) => ({ ...p, duration_minutes: e.target.value }))}
          dir="rtl"
        >
          {DURATION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <button type="submit" className="add-btn">
          {editingId ? 'تحديث' : 'إضافة'}
        </button>
        {editingId && (
          <button type="button" className="cancel-btn" onClick={resetForm}>
            إلغاء
          </button>
        )}
      </form>

      <div className="types-list">
        {types.length === 0 && <p className="empty">لا توجد أنواع حجز بعد</p>}
        {types.map((rt) => (
          <div key={rt.id} className="type-card">
            <div className="type-info">
              <span className="type-name">{rt.name}</span>
              <span className="type-price">{rt.price} &#8362;</span>
              <span className="type-duration">{formatDuration(rt.duration_minutes)}</span>
            </div>
            <div className="type-actions">
              <button className="edit-btn" onClick={() => startEdit(rt)}>تعديل</button>
              <button className="delete-btn" onClick={() => handleDelete(rt.id)}>حذف</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
